import { Observable, from as from$1, fromEvent as fromEvent$1, BehaviorSubject } from 'rxjs';
import { isRef, watch, shallowRef, readonly, ref } from 'vue-demi';
import { tryOnScopeDispose } from '@vueuse/shared';

function from(value, watchOptions) {
  if (isRef(value))
    return new Observable((subscriber) => watch(value, (val) => subscriber.next(val), watchOptions));
  return from$1(value);
}
function fromEvent(value, event) {
  if (isRef(value)) {
    return new Observable((subscriber) => {
      let innerSub;
      return watch(value, (element) => {
        innerSub == null ? void 0 : innerSub.unsubscribe();
        if (element instanceof HTMLElement) {
          innerSub = fromEvent$1(element, event).subscribe(subscriber);
          subscriber.add(innerSub);
        }
      }, { immediate: true });
    });
  }
  return fromEvent$1(value, event);
}

function toObserver(value) {
  return {
    next: (val) => {
      value.value = val;
    }
  };
}

function useExtractedObservable(source, extractor, options, watchOptions) {
  let subscription;
  tryOnScopeDispose(() => {
    subscription == null ? void 0 : subscription.unsubscribe();
    subscription = void 0;
  });
  const obsRef = shallowRef(options == null ? void 0 : options.initialValue);
  watch(source, (value, oldValue, onCleanup) => {
    subscription == null ? void 0 : subscription.unsubscribe();
    if (typeof value !== "undefined" && value !== null) {
      const observable = extractor(value, oldValue, onCleanup);
      subscription = observable.subscribe({
        error: (err) => {
          var _a;
          (_a = options == null ? void 0 : options.onError) == null ? void 0 : _a.call(options, err);
        },
        complete: () => {
          var _a;
          (_a = options == null ? void 0 : options.onComplete) == null ? void 0 : _a.call(options);
        },
        next: (val) => {
          obsRef.value = val;
        }
      });
    } else {
      subscription = void 0;
    }
  }, {
    immediate: true,
    ...watchOptions
  });
  return readonly(obsRef);
}

function useObservable(observable, options) {
  const value = ref(options == null ? void 0 : options.initialValue);
  const subscription = observable.subscribe({
    next: (val) => value.value = val,
    error: options == null ? void 0 : options.onError
  });
  tryOnScopeDispose(() => {
    subscription.unsubscribe();
  });
  return value;
}

function useSubject(subject, options) {
  const value = ref(
    subject instanceof BehaviorSubject ? subject.value : void 0
  );
  const subscription = subject.subscribe({
    next(val) {
      value.value = val;
    },
    error: options == null ? void 0 : options.onError
  });
  watch(value, (nextValue) => {
    subject.next(nextValue);
  });
  tryOnScopeDispose(() => {
    subscription.unsubscribe();
  });
  return value;
}

function useSubscription(subscription) {
  tryOnScopeDispose(() => {
    subscription.unsubscribe();
  });
}

function watchExtractedObservable(source, extractor, callback, subscriptionOptions, watchOptions) {
  let subscription;
  tryOnScopeDispose(() => {
    subscription == null ? void 0 : subscription.unsubscribe();
    subscription = void 0;
  });
  return watch(source, (value, oldValue, onCleanup) => {
    subscription == null ? void 0 : subscription.unsubscribe();
    if (typeof value !== "undefined" && value !== null) {
      const observable = extractor(value, oldValue, onCleanup);
      subscription = observable.subscribe({
        next: callback,
        error: subscriptionOptions == null ? void 0 : subscriptionOptions.onError,
        complete: subscriptionOptions == null ? void 0 : subscriptionOptions.onComplete
      });
    } else {
      subscription = void 0;
    }
  }, watchOptions);
}

export { from, fromEvent, toObserver, useExtractedObservable, useObservable, useSubject, useSubscription, watchExtractedObservable };
